import { environment } from "./../environments/environment";
import { VERSION } from "./../environments/version";
import { FooterBarComponent } from "./footer-bar/footer-bar.component";
import { RoutesService } from "./routes/routes.service";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgModule, Injectable, ErrorHandler, APP_INITIALIZER } from "@angular/core";
import { IntersectionObserverHooks, LAZYLOAD_IMAGE_HOOKS, LazyLoadImageModule } from "ng-lazyload-image";

import { AppRoutingModule } from "./routes/routing.module";

import { AppComponent } from "./app.component";
import { NavigationComponent } from "./navigation/navigation.component";
import { AngularFireModule } from "@angular/fire/compat";
import { InlineStyleComponent } from "./inline-style/inline-style.component";
import { InlineStyleModule } from "./inline-style/inline-style.module";
import { SharedModule } from "./shared/shared.module";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AnalyticsService } from "./analytics.service";

import * as Sentry from "@sentry/angular";
import { AngularFireAuthModule, USE_EMULATOR as AUTH_EMULATOR } from "@angular/fire/compat/auth";
import { AngularFirestoreModule, USE_EMULATOR as FIRESTORE_EMULATOR } from "@angular/fire/compat/firestore";
import { AngularFireDatabaseModule, USE_EMULATOR as DATABASE_EMULATOR } from "@angular/fire/compat/database";
import { AkitaNgDevtools } from "@datorama/akita-ngdevtools";
import { AkitaNgRouterStoreModule } from "@datorama/akita-ng-router-store";
import { AngularFireStorageModule, USE_EMULATOR as STORAGE_EMULATOR } from "@angular/fire/compat/storage";
import { provideFirebaseApp, initializeApp } from "@angular/fire/app";
import { connectFirestoreEmulator, getFirestore, provideFirestore } from "@angular/fire/firestore";
import { connectStorageEmulator, getStorage, provideStorage } from "@angular/fire/storage";
import { connectAuthEmulator, getAuth, provideAuth } from "@angular/fire/auth";
import { connectDatabaseEmulator, getDatabase, provideDatabase } from "@angular/fire/database";
import { NguiAutoCompleteModule } from "@ngui/auto-complete";
import { ServiceWorkerModule } from "@angular/service-worker";
import { HttpErrorResponse } from "@angular/common/http";
import { FormlyModule } from "@ngx-formly/core";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";
import * as LogRocket from "logrocket";
import { E2eFixtureService } from "./e2e-fixtures/e2e-fixture.service";

const e2eFirebaseEmulators = {
  host: "127.0.0.1",
  authPort: 9099,
  databasePort: 9000,
  firestorePort: 8080,
  storagePort: 9199
};

function getConfiguredFirestore() {
  const firestore = getFirestore();
  if (environment.e2e) {
    connectFirestoreEmulator(firestore, e2eFirebaseEmulators.host, e2eFirebaseEmulators.firestorePort);
  }
  return firestore;
}

function getConfiguredStorage() {
  const storage = getStorage();
  if (environment.e2e) {
    connectStorageEmulator(storage, e2eFirebaseEmulators.host, e2eFirebaseEmulators.storagePort);
  }
  return storage;
}

function getConfiguredDatabase() {
  const database = getDatabase();
  if (environment.e2e) {
    connectDatabaseEmulator(database, e2eFirebaseEmulators.host, e2eFirebaseEmulators.databasePort);
  }
  return database;
}

function getConfiguredAuth() {
  const auth = getAuth();
  if (environment.e2e) {
    connectAuthEmulator(auth, `http://${e2eFirebaseEmulators.host}:${e2eFirebaseEmulators.authPort}`, {
      disableWarnings: true
    });
  }
  return auth;
}

if (environment.sentryDsn) {
  Sentry.init({
    release: `${VERSION.version}-${VERSION.hash}`,
    dsn: environment.sentryDsn,
    environment: environment.envName,
    integrations: [
      Sentry.browserApiErrorsIntegration({
        XMLHttpRequest: false
      })
    ]
  });
}

if (environment.logRocketProject) {
  LogRocket.init(environment.logRocketProject, {
    release: `${VERSION.version}-${VERSION.hash}`
  });
}

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() {}

  extractError(error) {
    // Try to unwrap zone.js error.
    // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
    if (error && error.ngOriginalError) {
      error = error.ngOriginalError;
    }

    if (typeof error === "string" || error instanceof Error) {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      if (error.error instanceof Error) {
        return error.error;
      }

      if (error.error instanceof ErrorEvent) {
        return error.error.message;
      }

      if (typeof error.error === "string") {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }
      return error.message;
    }
    return error.originalError || error;
  }

  handleError(error) {
    let extractedError = this.extractError(error) || "Handled unknown error";

    if (!environment.production) {
      console.error(extractedError);
      return;
    }

    const chunkFailedMessage = /Loading chunk [\d]+ failed/;

    if (chunkFailedMessage.test(error.message)) {
      window.location.reload();
      return;
    }

    Sentry.captureException(extractedError);
    throw error;
  }
}

@NgModule({
  declarations: [AppComponent, NavigationComponent, FooterBarComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: "portal" }),
    BrowserAnimationsModule,
    LazyLoadImageModule,
    FormsModule,
    InlineStyleModule,
    SharedModule,
    AppRoutingModule,
    NguiAutoCompleteModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFirestoreModule,
    AngularFireStorageModule,
    AngularFireDatabaseModule,
    AngularFireAuthModule,
    environment.toggles.devTools ? AkitaNgDevtools.forRoot() : [],
    AkitaNgRouterStoreModule,
    ServiceWorkerModule.register("ngsw-worker.js", { enabled: environment.production }),
    ReactiveFormsModule,
    FormlyModule.forRoot({ extras: { lazyRender: true } }),
    FormlyBootstrapModule
  ],
  providers: [
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getConfiguredFirestore()),
    provideStorage(() => getConfiguredStorage()),
    provideDatabase(() => getConfiguredDatabase()),
    provideAuth(() => getConfiguredAuth()),
    RoutesService,
    AnalyticsService,
    ...(environment.e2e
      ? [
          { provide: FIRESTORE_EMULATOR, useValue: [e2eFirebaseEmulators.host, e2eFirebaseEmulators.firestorePort] },
          { provide: STORAGE_EMULATOR, useValue: [e2eFirebaseEmulators.host, e2eFirebaseEmulators.storagePort] },
          { provide: DATABASE_EMULATOR, useValue: [e2eFirebaseEmulators.host, e2eFirebaseEmulators.databasePort] },
          {
            provide: AUTH_EMULATOR,
            useValue: [`http://${e2eFirebaseEmulators.host}:${e2eFirebaseEmulators.authPort}`]
          }
        ]
      : []),
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [E2eFixtureService],
      useFactory: (fixtures: E2eFixtureService) => () => {
        if (environment.e2e) {
          fixtures.seed();
        }
      }
    },
    { provide: LAZYLOAD_IMAGE_HOOKS, useClass: IntersectionObserverHooks },
    { provide: ErrorHandler, useClass: SentryErrorHandler }
  ],
  bootstrap: [AppComponent, InlineStyleComponent]
})
export class AppModule {}
